<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import ResourceGroupsAddList from './ResourceGroupsAddList.vue';
import ResourceGroupsNewModal from './ResourceGroupsNewModal.vue';
import DfTabs from '/@elements/DfTabs/DfTabs.vue';
import {
  addResourceToFilter,
  addResourceToGroup,
  createResourceGroup,
  loadResourceFilter,
  loadResourceGroups,
  loadResources,
  removeResourceFromFilter,
  removeResourceFromGroup,
  removeResourceGroup,
  sortResourceFilter,
  sortResourceGroup,
  sortResourceGroupInFilter,
  updateResourceGroup,
} from '/@stores/resources';
import { useUserStore } from '/@stores/user';
import { loadValuelistByGuid } from '/@stores/valuelist';
import { Modules, Roles } from '/@types/ids';
import { Resource, ResourceFilter, ResourceGroup } from '/@types/resources';
import { Valuelist } from '/@types/valuelist';
import Icons from '/@utilities/icons';
import { compare } from '/@utilities/intl';
import { hasRole } from '/@utilities/user';

const props = defineProps<{
  filterId?: number;
}>();

const router = useRouter();
const filters = ref<Array<ResourceFilter>>([]);
const groups = ref<Array<ResourceGroup>>([]);
const resources = ref<Array<Resource>>([]);
const users = ref<Array<Resource>>([]);
const equipments = ref<Array<Resource>>([]);
const activeFilterId = ref<null | number>(props.filterId);
const newGroupName = ref('');
const editGroupSubmitting = ref(null);
const editGroupError = ref(null);
const loading = ref(new Set<string>());
const dragging = ref(null);
const draggingType = ref(null);
const droppingOn = ref(null);
const tagValueList = ref<Valuelist | null>(null);
const showFilterModal = ref(false);
const editingFilterId = ref<number | null>(null);
const isUserSpecific = ref(false);

const activeFilter = computed(() => filters.value.find(({ id }) => id === activeFilterId.value));
const sortedFilters = computed(() => [...filters.value].sort((a, b) => compare(a.name, b.name)));
const unassigned = computed(() => {
  if (activeFilterId.value == null) return { users: [], equipments: [] };
  const assignedGuids = new Set(resources.value.map((r) => r.guid));
  return {
    users: users.value.filter(({ guid }) => !assignedGuids.has(guid)),
    equipments: equipments.value.filter(({ guid }) => !assignedGuids.has(guid)),
  };
});
const userStore = useUserStore();
const filterNames = computed(() => new Set(filters.value.map((filter) => filter.name)));

const activeTab = ref<string | undefined>('users');
const tabs = computed(() => [
  { id: 'users', name: 'Brukere' },
  ...(userStore.hasModule(Modules.Utstyr) ? [{ id: 'equipments', name: 'Utstyr' }] : []),
]);

const typeOrder = { equipment: 0, user: 1, group: 2 };

function sortResources(data: Resource[]) {
  return [...data].sort((a, b) => {
    const typeDiff = (typeOrder[a.type] ?? 1) - (typeOrder[b.type] ?? 1);
    if (typeDiff !== 0) return typeDiff;

    const aIndex =
      a.sortIndex ?? a.filters.find(({ id }) => id === activeFilterId.value)?.sortIndex ?? 0;
    const bIndex =
      b.sortIndex ?? b.filters.find(({ id }) => id === activeFilterId.value)?.sortIndex ?? 0;

    return aIndex - bIndex;
  });
}

function refresh() {
  return Promise.all([
    loadResourceGroups({ filterId: activeFilterId.value }).then(
      (res) => (groups.value = res || []),
    ),
    loadResources({ type: 'user' }).then((res) => (users.value = res || [])),
    loadResources({ filterId: activeFilterId.value }).then(
      (data) => (resources.value = sortResources(data || [])),
    ),
    loadResourceFilter().then((res) => (filters.value = res || [])),
    loadResources({ type: 'equipment' }).then((res) => (equipments.value = res || [])),
  ]);
}

watch(activeFilterId, (filterId) => {
  router.push({ params: { filterId } });
  refresh();
});

refresh();

function findGroupFromResourceGuid(resourceGuid: string) {
  return groups.value.find((group) => group.resourceGuid === resourceGuid);
}

function findGroupFromSubresourceGuid(resourceGuid: string) {
  return groups.value.find((group) =>
    group.resources.some((subgroup) => subgroup.guid === resourceGuid),
  );
}

function onFilterCreated(filter: ResourceFilter) {
  filters.value.push(filter);
  activeFilterId.value = filter.id;
}

function onFilterUpdated(filter: ResourceFilter) {
  filters.value = filters.value.map((f) => (f.id === filter.id ? filter : f));
}

function onFilterRemoved() {
  filters.value = filters.value.filter(({ id }) => id !== editingFilterId.value);
  activeFilterId.value = null;
}

function addToFilter(guid: string) {
  loading.value.add(guid);

  addResourceToFilter(activeFilterId.value, guid)
    .then(() => refresh())
    .finally(() => loading.value.delete(guid));
}

function removeFromFilter(resourceGuid: string) {
  loading.value.add(resourceGuid);

  removeResourceFromFilter(activeFilterId.value, resourceGuid)
    .then(() => refresh())
    .finally(() => loading.value.delete(resourceGuid));
}

function createGroup(name: string) {
  createResourceGroup(name, activeFilterId.value).then(() => {
    refresh();
    newGroupName.value = '';
  });
}

function editGroup(resourceGuid: string, name: string, type: string) {
  const { id } = findGroupFromResourceGuid(resourceGuid);
  editGroupSubmitting.value = id;
  editGroupError.value = null;

  updateResourceGroup(id, { name, type, filterId: activeFilterId.value })
    .catch(() => {
      editGroupError.value = id;
    })
    .finally(() => {
      editGroupSubmitting.value = null;
      refresh();
    });
}

function removeGroup(resourceGuid: string) {
  const { id } = findGroupFromResourceGuid(resourceGuid);
  removeResourceGroup(id).then(() => refresh());
}

function onDragStart({ dataTransfer }: DragEvent, resource: Resource) {
  dragging.value = resource.guid;
  draggingType.value = resource.type;
  dataTransfer.effectAllowed = 'move';
  dataTransfer.setData('text/plain', JSON.stringify(resource));
}

function onDragEnd() {
  droppingOn.value = null;
  dragging.value = null;
  draggingType.value = null;
}

function onDragEnter(event: DragEvent, id: string | number) {
  if (dragging.value !== id) droppingOn.value = id;
}

function onDragLeave(event: DragEvent) {
  droppingOn.value = null;
}

async function onDrop({ dataTransfer }: DragEvent, droppedOnResource: Resource) {
  const draggedResource = JSON.parse(dataTransfer.getData('text/plain'));

  onDragEnd();

  // Terminate early if invalid drop
  if (draggedResource == null || draggedResource.guid === droppedOnResource.guid) return;
  if (draggedResource.type === 'group' && droppedOnResource.type !== 'group') return;

  loading.value.add(draggedResource.guid);

  switch (draggedResource.type) {
    case 'group': {
      const sortedResources = resources.value
        .filter(({ guid, type }) => type === 'group' && guid !== draggedResource.guid)
        .map(({ guid }) => guid);

      sortedResources.splice(
        sortedResources.findIndex((id) => id === droppedOnResource.guid) + 1,
        0,
        draggedResource.guid,
      );

      await sortResourceGroupInFilter(activeFilterId.value, sortedResources);
      break;
    }

    case 'user': {
      const draggedUserFromGroup = findGroupFromSubresourceGuid(draggedResource.guid);
      const droppedOnGroup =
        droppedOnResource.type === 'group'
          ? findGroupFromResourceGuid(droppedOnResource.guid)
          : findGroupFromSubresourceGuid(droppedOnResource.guid);

      // If moving from a group, to another, or to root
      if (draggedUserFromGroup != null && draggedUserFromGroup.id !== droppedOnGroup?.id) {
        await removeResourceFromGroup(draggedUserFromGroup.id, draggedResource.guid);
      }

      // If dropping on a different group, or moving from root
      if (droppedOnGroup != null && draggedUserFromGroup?.id !== droppedOnGroup.id) {
        await addResourceToGroup(droppedOnGroup.id, draggedResource.guid);
      }

      // If dropping group, sort group
      if (droppedOnGroup != null) {
        const sortedResources = droppedOnGroup.resources
          .filter(({ guid }) => guid !== draggedResource.guid)
          .map(({ guid }) => guid);

        sortedResources.splice(
          sortedResources.findIndex((guid) => guid === droppedOnResource.guid) + 1,
          0,
          draggedResource.guid,
        );

        await sortResourceGroup(droppedOnGroup.id, sortedResources);
      }

      // Else sort root
      else {
        const sortedResources = activeFilter.value?.resources
          .filter(({ guid }) => guid !== draggedResource.guid)
          .map(({ guid }) => guid);

        sortedResources?.splice(
          sortedResources.findIndex((guid) => guid === droppedOnResource.guid) + 1,
          0,
          draggedResource.guid,
        );

        await sortResourceFilter(activeFilterId.value, sortedResources);
      }

      break;
    }

    case 'equipment':
      loading.value.delete(draggedResource.guid);
      return;
  }

  // Get the updated users and groups then remove loading state
  refresh().finally(() => {
    loading.value.delete(draggedResource.guid);
  });
}

function globalOnDrop() {
  dragging.value = null;
}

function openNewFilterModal(isPersonal: boolean) {
  isUserSpecific.value = isPersonal;
  editingFilterId.value = null;
  showFilterModal.value = true;
}

function openEditFilterModal() {
  if (!activeFilter.value) return;
  isUserSpecific.value = activeFilter.value.isUserSpecific;
  editingFilterId.value = activeFilterId.value;
  showFilterModal.value = true;
}

function closeFilterModal() {
  showFilterModal.value = false;
  isUserSpecific.value = false;
  editingFilterId.value = null;
}

loadValuelistByGuid({ valuelistGuid: 'c6e62101-a648-4c88-82c4-61cb2ac30e5c' }).then((res) => {
  if (!res) return;
  tagValueList.value = res;
});

onMounted(() => addEventListener('drop', globalOnDrop));
onUnmounted(() => removeEventListener('drop', globalOnDrop));
</script>

<template>
  <div class="groups-page" :class="{ 'groups-page--dragging': dragging != null }">
    <header class="groups-page__header">
      <h1>{{ activeFilter?.name ?? '(Velg gruppe)' }}</h1>

      <span class="badge" v-if="activeFilter?.type">{{ activeFilter.type }}</span>

      <df-button elevate :disabled="activeFilter == null" v-on:click="openEditFilterModal()">
        <template v-slot:icon>
          <df-icon code="f304" />
        </template>
      </df-button>

      <df-dropdown>
        <template v-slot:trigger="{ toggle }">
          <df-button elevate v-on:click="toggle()">
            <template v-slot:icon>
              <df-icon code="f107" />
            </template>
          </df-button>
        </template>

        <template v-slot:default="{ close }">
          <df-dropdown-item
            v-for="filter in sortedFilters"
            :key="filter.id"
            v-on:click="activeFilterId = filter.id"
            no-icon
            :disabled="
              userStore.user == null ||
              (!hasRole(userStore.user, Roles.RessursAnsvarlig) && !filter.isUserSpecific)
            "
          >
            <div>
              {{ filter.name }}
              <span class="badge" v-if="filter.type">{{ filter.type }}</span>
              <span class="badge badge--info" v-if="filter.isUserSpecific">Personlig</span>
            </div>
          </df-dropdown-item>
        </template>
      </df-dropdown>

      <df-dropdown>
        <template v-slot:trigger="{ toggle }">
          <df-button elevate style="margin-left: auto" v-on:click="toggle()">
            Ny gruppe

            <template v-slot:icon>
              <df-icon code="2b" />
            </template>
          </df-button>
        </template>

        <df-dropdown-item
          v-on:click="openNewFilterModal(false)"
          :disabled="userStore.user == null || !hasRole(userStore.user, Roles.RessursAnsvarlig)"
        >
          <template v-slot:icon>
            <df-icon code="2b" />
          </template>

          Firmagruppe
        </df-dropdown-item>

        <df-dropdown-item v-on:click="openNewFilterModal(true)">
          <template v-slot:icon>
            <df-icon code="2b" />
          </template>

          Personlig gruppe
        </df-dropdown-item>

        <hr />

        <df-dropdown-item-block>
          <template v-slot:icon>
            <df-icon code="f05a" />
          </template>

          Firmagrupper er synlig for alle, og opprettes av ressursansvarlig.
        </df-dropdown-item-block>
      </df-dropdown>
    </header>

    <div class="group-page__info-a info">
      <p>
        Følgende er en liste over ressurser knyttet til gruppen, og hvilken undergruppe de tilhører.
        Gruppe styrer hvilke ressurser du ser i «Planlegg avtaler», mens undergrupper tilsvarer
        inndelingen i tabellen.
      </p>
      <br />
      <p v-if="activeFilter != null && resources.length > 30">
        <df-icon code="f06a" />
        Det er nå mer enn 30 ressurser på gruppen. Vi anbefaler å dele opp i mindre grupper da det
        kan oppleves litt seigt om man har mange avtaler per ressurs og ser på store tidsvinduer
        (f.eks. «kvartal») i «Planlegg avtaler».
      </p>
    </div>

    <ol class="group-page__list-a resource-list card" v-if="activeFilter != null">
      <template v-for="resource in resources" :key="resource.guid">
        <li
          v-if="resource.type === 'user'"
          class="resource-list__item resource-list__item--draggable"
          draggable="true"
          :class="{
            'resource-list__item--dropping': droppingOn === resource.guid,
            'resource-list__item--disabled': draggingType === 'group' || dragging === resource.guid,
            'resource-list__item--loading': loading.has(resource.guid),
          }"
          v-on:dragenter="onDragEnter($event, resource.guid)"
          v-on:dragleave="onDragLeave($event)"
          v-on:dragstart="onDragStart($event, resource)"
          v-on:dragend="onDragEnd()"
          v-on:dragover.prevent
          v-on:drop="onDrop($event, resource)"
        >
          <div class="loading-icon" v-if="loading.has(resource.guid)"></div>
          <df-icon code="e411" solid v-else />

          <span class="resource-list__item-name">{{ resource.name }}</span>

          <df-button
            v-on:click="removeFromFilter(resource.guid)"
            :disabled="loading.has(resource.guid)"
          >
            <template v-slot:icon>
              <df-icon code="f00d" />
            </template>
          </df-button>
        </li>

        <li
          v-else-if="resource.type === 'equipment'"
          class="resource-list__item"
          :class="{ 'resource-list__item--loading': loading.has(resource.guid) }"
        >
          <div class="loading-icon" v-if="loading.has(resource.guid)"></div>
          <df-icon :code="Icons.Wrench" v-else />

          <span class="resource-list__item-name">{{ resource.name }}</span>

          <df-button
            v-on:click="removeFromFilter(resource.guid)"
            :disabled="loading.has(resource.guid)"
          >
            <template v-slot:icon>
              <df-icon :code="Icons.Xmark" />
            </template>
          </df-button>
        </li>

        <template v-else-if="resource.type === 'group'">
          <li class="resource-list__group">
            <!-- Dragging groups are temporary disabled until we find a solution for sortIndex on backend -->
            <!--
          draggable="true"
          v-on:dragstart="onDragStart($event, resource)"
        -->
            <div
              class="resource-list__item"
              :class="{
                'resource-list__item--dropping': droppingOn === resource.guid,
                'resource-list__item--disabled': dragging === resource.guid,
              }"
              v-on:dragenter="onDragEnter($event, resource.guid)"
              v-on:dragleave="onDragLeave($event)"
              draggable="true"
              v-on:dragstart="onDragStart($event, resource)"
              v-on:dragend="onDragEnd()"
              v-on:dragover.prevent
              v-on:drop="onDrop($event, resource)"
            >
              <df-icon :code="Icons.UserGroup" />

              <df-input
                :model-value="resource.name"
                v-on:update:model-value="editGroup(resource.guid, $event, resource.groupType)"
                style="place-self: stretch"
              />

              <div class="loading-icon" v-if="editGroupSubmitting === resource.guid"></div>

              <div class="badge" v-if="resource.groupType">{{ resource.groupType }}</div>

              <df-dropdown>
                <template v-slot:trigger="{ toggle }">
                  <df-button v-on:click="toggle">
                    <template v-slot:icon>
                      <df-icon :code="Icons.Tag" />
                    </template>
                  </df-button>
                </template>

                <df-dropdown-item-input
                  placeholder="Skriv her"
                  v-on:submit="editGroup(resource.guid, resource.name, $event)"
                >
                  <template v-slot:icon>
                    <df-icon :code="Icons.Tag" />
                  </template>
                </df-dropdown-item-input>

                <hr style="height: 0; border: none; border-top: 1px solid var(--color-divider)" />

                <df-dropdown-item-block>
                  <div class="title-sm">Forslagsverdier</div>
                </df-dropdown-item-block>

                <df-dropdown-item v-on:click="editGroup(resource.guid, resource.name, '')">
                  <template v-slot:icon>
                    <df-icon :code="Icons.Xmark" />
                  </template>
                  Ingen
                </df-dropdown-item>

                <df-dropdown-item
                  v-for="[itemId, item] in tagValueList.valuelistItems"
                  :key="itemId"
                  v-on:click="editGroup(resource.guid, resource.name, item.name)"
                >
                  {{ item.name }}
                </df-dropdown-item>
              </df-dropdown>

              <df-button v-on:click="removeGroup(resource.guid)">
                <template v-slot:icon>
                  <df-icon :code="Icons.TrashCan" />
                </template>
              </df-button>
            </div>

            <ul class="resource-list resource-list--grouped">
              <li
                class="resource-list__item resource-list__item--draggable"
                draggable="true"
                :class="{
                  'resource-list__item--dropping': droppingOn === subresource.guid,
                  'resource-list__item--disabled':
                    draggingType === 'group' || dragging === resource.guid,
                  'resource-list__item--loading': loading.has(subresource.guid),
                }"
                :key="subresource.guid"
                v-for="subresource in resource.subresources"
                v-on:dragenter="onDragEnter($event, subresource.guid)"
                v-on:dragleave="onDragLeave($event)"
                v-on:dragstart="onDragStart($event, subresource)"
                v-on:dragend="onDragEnd()"
                v-on:dragover.prevent
                v-on:drop="onDrop($event, subresource)"
              >
                <div class="loading-icon" v-if="loading.has(subresource.guid)"></div>
                <df-icon code="e411" solid v-else />

                <span class="resource-list__item-name">{{ subresource.name }}</span>

                <df-button
                  v-on:click="removeFromFilter(subresource.guid)"
                  :disabled="loading.has(subresource.guid)"
                >
                  <template v-slot:icon>
                    <df-icon :code="Icons.Xmark" />
                  </template>
                </df-button>
              </li>
            </ul>
          </li>
        </template>
      </template>

      <li class="resource-list__item">
        <df-icon code="f500" />

        <form
          v-on:submit.prevent="createGroup(newGroupName)"
          style="display: grid; gap: var(--gap-md); grid-template-columns: 1fr max-content"
        >
          <df-input placeholder="Ny undergruppe" v-model="newGroupName" />

          <df-button primary elevate type="submit" :disabled="newGroupName.length === 0">
            Opprett
            <template v-slot:icon>
              <df-icon code="2b" />
            </template>
          </df-button>
        </form>
      </li>
    </ol>

    <div class="card" v-else>Velg gruppe for å redigere</div>

    <p class="group-page__info-b info">
      Følgende er en liste over ledige ressurser. Bruk fanene for å velge mellom brukere og utstyr,
      og klikk på en ressurs for å legge den til i gruppen.
    </p>

    <df-tabs class="group-page__list-b" :tabs="tabs" v-model="activeTab">
      <template #tab:users>
        <ResourceGroupsAddList
          :items="unassigned.users"
          :filters="filters"
          :loadingGuids="loading"
          @add="addToFilter"
        />
      </template>

      <template #tab:equipments>
        <ResourceGroupsAddList
          :items="unassigned.equipments"
          :filters="filters"
          :loadingGuids="loading"
          @add="addToFilter"
        />
      </template>
    </df-tabs>

    <ResourceGroupsNewModal
      v-if="showFilterModal"
      :initial-filter="editingFilterId != null ? activeFilter : undefined"
      :is-personal="isUserSpecific"
      :tag-value-list="tagValueList"
      :filterNames="filterNames"
      v-on:created="onFilterCreated"
      v-on:updated="onFilterUpdated"
      v-on:removed="onFilterRemoved"
      v-on:close="closeFilterModal"
    />
  </div>
</template>

<style scoped>
.groups-page {
  display: grid;
  grid-template-columns: minmax(0, 60ch) minmax(0, 40ch);
  grid-template-areas:
    'header header'
    'info-a info-b'
    'list-a list-b';
  place-content: center;
  padding: var(--gap-lg) var(--gap-md);
  gap: var(--gap-lg) calc(var(--gap-lg) * 2);
}

.groups-page__header {
  grid-area: header;
  display: flex;
  align-items: center;
  gap: var(--gap-md);
}

.group-page__info-a {
  grid-area: info-a;
}

.group-page__info-b {
  grid-area: info-b;
}

.group-page__list-a {
  grid-area: list-a;
}

.group-page__list-b {
  grid-area: list-b;
  display: flex;
  flex-direction: column;
  gap: var(--gap-md);
}

.resource-list {
  display: flex;
  flex-direction: column;
  gap: var(--gap-md);
}

.resource-list--grouped {
  border-left: 1px solid var(--color-border);
  padding-left: var(--gap-md);
  margin-left: 1.5rem;
}

.resource-list__item {
  position: relative;
  display: grid;
  grid-template-columns: max-content 1fr;
  grid-auto-flow: column;
  grid-auto-columns: max-content;
  align-items: center;
  gap: var(--gap-md);

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    height: 0.5rem;
    background-color: transparent;
    border-radius: 1rem;
    pointer-events: none;
  }
}

.resource-list__item--draggable {
  cursor: grab;
}

.resource-list__item--disabled,
.resource-list__item--loading {
  opacity: 0.5;
}

.resource-list__item--loading {
  pointer-events: none;
}

.groups-page--dragging .resource-list__item {
  & > * {
    pointer-events: none;
  }
}

.resource-list__item--dropping {
  &::after {
    background-color: orange;
  }
}

.resource-list__group {
  list-style: none;
  display: grid;
  gap: var(--gap-md);
}

.resource-list__item-name {
  pointer-events: none;
}

.title-badge {
  background-color: var(--color-darken);
  border-radius: var(--radius-sm);
  font-weight: normal;
  display: inline-block;
  padding: 0 var(--gap-sm);
}
</style>
